import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatNumber } from '@/utils/formatNumber';
import { 
  Download,
  Users,
  GitBranch,
  FileText,
  TrendingUp,
  TrendingDown,
  Minus,
  Star,
  Activity
} from 'lucide-react';

/**
 * UsageMetricsCards - Key performance indicator cards for materials usage
 * 
 * Displays:
 * - Total downloads with trend indicator
 * - Unique users accessing materials
 * - Active versions in circulation
 * - Programs covered
 * - Top performing materials
 */
const UsageMetricsCards = ({ data }) => {
  if (!data) {
    return (
      <div className="text-center py-4 text-gray-500">
        No usage metrics available
      </div>
    );
  }

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'down':
        return <TrendingDown className="h-4 w-4 text-red-600" />;
      default:
        return <Minus className="h-4 w-4 text-gray-600" />;
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'up':
        return 'text-green-600';
      case 'down':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="space-y-6">
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Download className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-500">Total Downloads</span>
            </div>
            <div className="text-2xl font-bold mt-3">{formatNumber(data.totalDownloads)}</div>
            <div className="flex items-center space-x-1 mt-2">
              <TrendingUp className="h-3 w-3 text-green-600" />
              <p className="text-xs text-green-600">+12% from last month</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Users className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-gray-500">Unique Users</span>
            </div>
            <div className="text-2xl font-bold mt-3">{formatNumber(data.uniqueUsers)}</div>
            <div className="flex items-center space-x-1 mt-2">
              <TrendingUp className="h-3 w-3 text-green-600" />
              <p className="text-xs text-green-600">+8% from last month</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <GitBranch className="h-5 w-5 text-purple-600" />
              <span className="text-sm font-medium text-gray-500">Active Versions</span>
            </div>
            <div className="text-2xl font-bold mt-3">{data.activeVersions}</div>
            <p className="text-xs text-gray-500 mt-2">Currently in circulation</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <FileText className="h-5 w-5 text-orange-600" />
              <span className="text-sm font-medium text-gray-500">Programs Covered</span>
            </div>
            <div className="text-2xl font-bold mt-3">{data.programsCovered}</div>
            <p className="text-xs text-gray-500 mt-2">Across all materials</p>
          </CardContent>
        </Card>
      </div>

      {/* Top Materials */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center space-x-2 mb-6">
            <Star className="h-5 w-5 text-yellow-600" />
            <h3 className="text-lg font-semibold text-gray-900">Top Performing Materials</h3>
            <p className="text-sm text-gray-500">Most downloaded this period</p>
          </div>
          
          <div className="space-y-4">
            {data.topMaterials.map((material, index) => (
              <div 
                key={material.name}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-800 rounded-full text-sm font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 truncate">
                        {material.name}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {material.program}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 ml-11">
                    <div className="flex items-center space-x-1">
                      <Download className="h-3 w-3 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {formatNumber(material.downloads)} downloads
                      </span>
                    </div>
                    <Badge variant="secondary" className="text-xs font-mono">
                      {material.version}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {getTrendIcon(material.trend)}
                  <div className={`text-sm font-medium ${getTrendColor(material.trend)}`}>
                    {material.trend === 'up' ? '+15%' : 
                     material.trend === 'down' ? '-5%' : '0%'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="text-center p-4 bg-blue-50 rounded-lg">
          <div className="flex items-center justify-center mb-2">
            <Activity className="h-5 w-5 text-blue-600" />
          </div>
          <div className="text-lg font-bold text-blue-600">
            {Math.round(data.totalDownloads / data.uniqueUsers * 10) / 10}
          </div>
          <p className="text-xs text-blue-700 font-medium">Avg Downloads per User</p>
        </div>

        <div className="text-center p-4 bg-green-50 rounded-lg">
          <div className="flex items-center justify-center mb-2">
            <TrendingUp className="h-5 w-5 text-green-600" />
          </div>
          <div className="text-lg font-bold text-green-600">94%</div>
          <p className="text-xs text-green-700 font-medium">User Satisfaction</p>
        </div>

        <div className="text-center p-4 bg-purple-50 rounded-lg">
          <div className="flex items-center justify-center mb-2">
            <GitBranch className="h-5 w-5 text-purple-600" />
          </div>
          <div className="text-lg font-bold text-purple-600">
            {Math.round(data.activeVersions / data.programsCovered * 10) / 10}
          </div>
          <p className="text-xs text-purple-700 font-medium">Versions per Program</p>
        </div>

        <div className="text-center p-4 bg-orange-50 rounded-lg">
          <div className="flex items-center justify-center mb-2">
            <FileText className="h-5 w-5 text-orange-600" />
          </div>
          <div className="text-lg font-bold text-orange-600">
            {data.topMaterials.length}
          </div>
          <p className="text-xs text-orange-700 font-medium">Popular Materials</p>
        </div>
      </div>

      {/* Engagement Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6 text-center">
            <div className="flex items-center justify-center mb-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                <Download className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">
              {Math.round(data.totalDownloads / 30)}
            </div>
            <p className="text-sm text-gray-500">Daily Download Average</p>
            <div className="flex items-center justify-center space-x-1 mt-2">
              <TrendingUp className="h-3 w-3 text-green-600" />
              <span className="text-xs text-green-600 font-medium">+5.2% this week</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 text-center">
            <div className="flex items-center justify-center mb-3">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
                <Users className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">
              {Math.round(data.uniqueUsers * 0.72)}
            </div>
            <p className="text-sm text-gray-500">Return User Rate</p>
            <div className="flex items-center justify-center space-x-1 mt-2">
              <TrendingUp className="h-3 w-3 text-green-600" />
              <span className="text-xs text-green-600 font-medium">+2.8% this week</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 text-center">
            <div className="flex items-center justify-center mb-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                <Activity className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">
              {Math.round((data.totalDownloads / data.uniqueUsers) * 100) / 100}
            </div>
            <p className="text-sm text-gray-500">Engagement Score</p>
            <div className="flex items-center justify-center space-x-1 mt-2">
              <TrendingUp className="h-3 w-3 text-green-600" />
              <span className="text-xs text-green-600 font-medium">+1.4% this week</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UsageMetricsCards;